import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { BotContext, GameResult } from "@topzle/types";
import { z } from "zod";

export const rockPaperScissorsGestures = ["rock", "paper", "scissors"] as const;
const gestureSchema = z.enum(rockPaperScissorsGestures);
const throwSourceSchema = z.enum(["camera", "manual", "bot"]);
const throwSchema = z.object({
  gesture: gestureSchema,
  confidence: z.number().min(0).max(1),
  source: throwSourceSchema,
  capturedAt: z.number(),
});
const roundHistorySchema = z.object({
  round: z.number().int().positive(),
  gestures: z.record(z.string(), gestureSchema),
  winnerId: z.string().nullable(),
  reason: z.enum(["win", "draw", "timeout"]),
});

export const rockPaperScissorsStateSchema = z.object({
  players: z.array(z.string()).length(2),
  phase: z.enum(["ready", "countdown", "reveal", "finished"]),
  round: z.number().int().positive(),
  targetScore: z.number().int().min(1).max(7),
  countdownSeconds: z.number().int().min(2).max(5),
  readyIds: z.array(z.string()).max(2),
  throws: z.record(z.string(), throwSchema),
  scores: z.record(z.string(), z.number().int().nonnegative()),
  countdownStartedAt: z.number().nullable(),
  countdownEndsAt: z.number().nullable(),
  captureClosesAt: z.number().nullable(),
  nextRoundAt: z.number().nullable().default(null),
  roundWinnerId: z.string().nullable(),
  roundReason: z.enum(["win", "draw", "timeout"]).nullable(),
  history: z.array(roundHistorySchema).max(25),
});

export const rockPaperScissorsEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ready") }),
  z.object({
    type: z.literal("throw"),
    gesture: gestureSchema,
    confidence: z.number().min(0).max(1),
    source: throwSourceSchema,
  }),
  z.object({ type: z.literal("next_round") }),
]);

export const rockPaperScissorsSettingsSchema = z.object({
  targetScore: z.number().int().min(1).max(7).default(3),
  countdownSeconds: z.number().int().min(2).max(5).default(3),
});

export type RockPaperScissorsGesture = z.infer<typeof gestureSchema>;
export type RockPaperScissorsThrow = z.infer<typeof throwSchema>;
export type RockPaperScissorsState = z.infer<typeof rockPaperScissorsStateSchema>;
export type RockPaperScissorsEvent = z.infer<typeof rockPaperScissorsEventSchema>;
export type RockPaperScissorsSettings = z.infer<typeof rockPaperScissorsSettingsSchema>;

export interface RockPaperScissorsPublicThrow {
  gesture: RockPaperScissorsGesture | null;
  confidence: number | null;
  source: RockPaperScissorsThrow["source"] | null;
  capturedAt: number | null;
  locked: boolean;
}

export type RockPaperScissorsPublicState = Omit<RockPaperScissorsState, "throws"> & {
  throws: Record<string, RockPaperScissorsPublicThrow>;
};

export const ROCK_PAPER_SCISSORS_REVEAL_MS = 3_500;

export function gestureWinner(
  left: RockPaperScissorsGesture,
  right: RockPaperScissorsGesture,
): 0 | 1 | 2 {
  if (left === right) return 0;
  if (
    (left === "rock" && right === "scissors") ||
    (left === "paper" && right === "rock") ||
    (left === "scissors" && right === "paper")
  )
    return 1;
  return 2;
}

function beginCountdown(state: RockPaperScissorsState, now: number): RockPaperScissorsState {
  const countdownEndsAt = now + state.countdownSeconds * 1000;
  return {
    ...state,
    phase: "countdown",
    countdownStartedAt: now,
    countdownEndsAt,
    captureClosesAt: countdownEndsAt + 4_000,
    nextRoundAt: null,
    roundWinnerId: null,
    roundReason: null,
    throws: {},
  };
}

function resolveRound(
  state: RockPaperScissorsState,
  now: number,
  timedOut = false,
): RockPaperScissorsState {
  const [leftId = "", rightId = ""] = state.players;
  const left = state.throws[leftId];
  const right = state.throws[rightId];
  let winnerId: string | null = null;
  let reason: RockPaperScissorsState["roundReason"] = timedOut ? "timeout" : "draw";
  if (left && right) {
    const winner = gestureWinner(left.gesture, right.gesture);
    winnerId = winner === 1 ? leftId : winner === 2 ? rightId : null;
    reason = winnerId ? "win" : "draw";
  } else if (left || right) {
    winnerId = left ? leftId : rightId;
    reason = "timeout";
  }
  const scores = { ...state.scores };
  if (winnerId) scores[winnerId] = (scores[winnerId] ?? 0) + 1;
  const finished = winnerId ? (scores[winnerId] ?? 0) >= state.targetScore : false;
  const gestures = Object.fromEntries(
    Object.entries(state.throws).map(([playerId, value]) => [playerId, value.gesture]),
  );
  return {
    ...state,
    phase: finished ? "finished" : "reveal",
    scores,
    captureClosesAt: now,
    nextRoundAt: finished ? null : now + ROCK_PAPER_SCISSORS_REVEAL_MS,
    roundWinnerId: winnerId,
    roundReason: reason,
    history: [
      ...state.history,
      { round: state.round, gestures, winnerId, reason: reason ?? "draw" },
    ].slice(-25),
  };
}

function beginNextRound(state: RockPaperScissorsState, now: number): RockPaperScissorsState {
  return beginCountdown(
    {
      ...state,
      phase: "ready",
      round: state.round + 1,
      readyIds: [...state.players],
      throws: {},
      countdownStartedAt: null,
      countdownEndsAt: null,
      captureClosesAt: null,
      nextRoundAt: null,
      roundWinnerId: null,
      roundReason: null,
    },
    now,
  );
}

function counterGesture(gesture: RockPaperScissorsGesture): RockPaperScissorsGesture {
  return gesture === "rock" ? "paper" : gesture === "paper" ? "scissors" : "rock";
}

export function chooseRockPaperScissorsBotGesture(
  state: RockPaperScissorsState,
  botContext: BotContext,
): RockPaperScissorsGesture {
  const opponentId = state.players.find((playerId) => playerId !== botContext.playerId) ?? "";
  const opponentHistory = state.history.flatMap((entry) => {
    const gesture = entry.gestures[opponentId];
    return gesture ? [gesture] : [];
  });
  const fallback =
    rockPaperScissorsGestures[
      Math.abs(state.round * 17 + Math.floor(botContext.now / 1000)) %
        rockPaperScissorsGestures.length
    ] ?? "rock";
  const difficulty =
    botContext.policy === "uniform"
      ? "easy"
      : botContext.policy === "transition"
        ? "medium"
        : botContext.policy === "frequency"
          ? "hard"
          : botContext.difficulty;
  if (difficulty === "easy" || opponentHistory.length === 0) return fallback;
  if (difficulty === "medium") {
    const latest = opponentHistory.at(-1);
    return latest && state.round % 3 !== 0 ? counterGesture(latest) : fallback;
  }
  const counts = Object.fromEntries(
    rockPaperScissorsGestures.map((gesture) => [gesture, 0]),
  ) as Record<RockPaperScissorsGesture, number>;
  for (const gesture of opponentHistory) counts[gesture] += 1;
  const predicted = [...rockPaperScissorsGestures].sort(
    (left, right) => counts[right] - counts[left],
  )[0];
  return predicted ? counterGesture(predicted) : fallback;
}

function publicState(
  state: RockPaperScissorsState,
  viewerId: string | null,
): RockPaperScissorsPublicState {
  const revealed = state.phase === "reveal" || state.phase === "finished";
  return {
    ...state,
    throws: Object.fromEntries(
      state.players.map((playerId) => {
        const value = state.throws[playerId];
        const visible = Boolean(value) && (revealed || playerId === viewerId);
        return [
          playerId,
          {
            gesture: visible ? (value?.gesture ?? null) : null,
            confidence: visible ? (value?.confidence ?? null) : null,
            source: visible ? (value?.source ?? null) : null,
            capturedAt: visible ? (value?.capturedAt ?? null) : null,
            locked: Boolean(value),
          },
        ];
      }),
    ),
  };
}

export const rockPaperScissorsGame: TopzleGameDefinition<
  RockPaperScissorsState,
  RockPaperScissorsEvent,
  RockPaperScissorsSettings
> = {
  id: "rock-paper-scissors",
  version: "1.0.0",
  name: "Live Rock Paper Scissors",
  shortDescription: "Show your hand on camera and let on-device MediaPipe call the throw.",
  longDescription:
    "A synchronized live camera showdown with private on-device hand recognition, hidden throws, adaptive bots, and server-authoritative scoring.",
  category: "party",
  icon: "hand",
  cover: "rock-paper-scissors",
  minPlayers: 2,
  maxPlayers: 2,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: false,
  supportsAiPlayers: true,
  supportsLocalAi: true,
  supportsCloudAi: false,
  supportsTournaments: true,
  supportsPersistence: true,
  requiredCapabilities: ["video", "local_ai"],
  supportedLayouts: ["bubbles", "game_fullscreen", "split", "picture_in_picture", "avatar_only"],
  preferredLayout: "split",
  stateSchema: rockPaperScissorsStateSchema,
  eventSchema: rockPaperScissorsEventSchema,
  settingsSchema: rockPaperScissorsSettingsSchema,
  createInitialState(context, settings) {
    const [left = "player-one", right = "player-two"] = context.playerIds;
    return {
      players: [left, right],
      phase: "ready",
      round: 1,
      targetScore: settings.targetScore,
      countdownSeconds: settings.countdownSeconds,
      readyIds: [],
      throws: {},
      scores: { [left]: 0, [right]: 0 },
      countdownStartedAt: null,
      countdownEndsAt: null,
      captureClosesAt: null,
      nextRoundAt: null,
      roundWinnerId: null,
      roundReason: null,
      history: [],
    };
  },
  validateEvent(state, event, context) {
    const actorId = context.actorId ?? "";
    if (!state.players.includes(actorId))
      return { valid: false, code: "spectator", message: "Spectators cannot submit a throw" };
    if (event.type === "ready")
      return {
        valid: state.phase === "ready" && !state.readyIds.includes(actorId),
        code: "already_ready",
        message: "You are already ready",
      };
    if (event.type === "next_round")
      return {
        valid:
          state.phase === "reveal" &&
          (state.nextRoundAt ?? Number.POSITIVE_INFINITY) <= context.now,
        code: state.phase === "reveal" ? "round_timer_active" : "round_active",
        message:
          state.phase === "reveal"
            ? "The next throw starts automatically when the timer ends"
            : "Finish this throw first",
      };
    if (state.phase !== "countdown")
      return { valid: false, code: "not_capturing", message: "Wait for the countdown" };
    if (actorId.startsWith("ai:") !== (event.source === "bot"))
      return { valid: false, code: "invalid_source", message: "That throw source is not valid" };
    if (state.throws[actorId])
      return { valid: false, code: "throw_locked", message: "Your throw is already locked" };
    if (!actorId.startsWith("ai:") && context.now < (state.countdownEndsAt ?? 0) - 900)
      return { valid: false, code: "too_early", message: "Hold your hand until the final count" };
    if (context.now > (state.captureClosesAt ?? 0))
      return { valid: false, code: "capture_closed", message: "The capture window has closed" };
    return { valid: true };
  },
  reduceEvent(state, event, context) {
    const actorId = context.actorId ?? "";
    if (event.type === "ready") {
      const readyIds = [...new Set([...state.readyIds, actorId])];
      const readyState = { ...state, readyIds };
      return readyIds.length === state.players.length
        ? beginCountdown(readyState, context.now)
        : readyState;
    }
    if (event.type === "next_round") return beginNextRound(state, context.now);
    const throws = {
      ...state.throws,
      [actorId]: {
        gesture: event.gesture,
        confidence: event.confidence,
        source: event.source,
        capturedAt: context.now,
      },
    };
    const thrownState = { ...state, throws };
    return Object.keys(throws).length === state.players.length
      ? resolveRound(thrownState, context.now)
      : thrownState;
  },
  getPublicState: (state, viewer) => publicState(state, viewer.viewerId),
  getPrivatePlayerState(state, playerId) {
    return { throw: state.throws[playerId] ?? null };
  },
  getSpectatorState: (state) => publicState(state, null),
  getAvailableActions(state, playerId) {
    if (!state.players.includes(playerId)) return [];
    if (state.phase === "ready" && !state.readyIds.includes(playerId)) return [{ type: "ready" }];
    if (state.phase === "countdown" && !state.throws[playerId])
      return rockPaperScissorsGestures.map((gesture) => ({ type: "throw", gesture }));
    return [];
  },
  calculateResult(state): GameResult | null {
    if (state.phase !== "finished") return null;
    const winnerId = state.players.find((playerId) => state.scores[playerId] === state.targetScore);
    return {
      status: winnerId ? "completed" : "draw",
      winnerIds: winnerId ? [winnerId] : [],
      scores: state.scores,
      reason: "target_score_reached",
    };
  },
  async createBotAction(state, botContext) {
    if (state.phase === "ready") return { type: "ready" };
    if (state.phase === "reveal") return { type: "next_round" };
    return {
      type: "throw",
      gesture: chooseRockPaperScissorsBotGesture(state, botContext),
      confidence: 1,
      source: "bot",
    };
  },
  getNextDeadline(state) {
    if (state.phase === "countdown") return state.captureClosesAt;
    if (state.phase === "reveal")
      return (
        state.nextRoundAt ??
        (state.captureClosesAt === null
          ? null
          : state.captureClosesAt + ROCK_PAPER_SCISSORS_REVEAL_MS)
      );
    return null;
  },
  handleDeadline(state, context) {
    if (
      state.phase === "countdown" &&
      state.captureClosesAt !== null &&
      state.captureClosesAt <= context.now
    )
      return resolveRound(state, context.now, true);
    if (state.phase === "reveal") {
      const nextRoundAt =
        state.nextRoundAt ??
        (state.captureClosesAt === null
          ? null
          : state.captureClosesAt + ROCK_PAPER_SCISSORS_REVEAL_MS);
      if (nextRoundAt !== null && nextRoundAt <= context.now)
        return beginNextRound(state, context.now);
    }
    return state;
  },
  handlePlayerDeparture(state, departure) {
    const playerIndex = state.players.indexOf(departure.playerId);
    if (playerIndex < 0 || !departure.replacementPlayerId) return null;
    const replacementPlayerId = departure.replacementPlayerId;
    const players = [...state.players];
    players[playerIndex] = replacementPlayerId;
    const scores = {
      ...state.scores,
      [replacementPlayerId]: state.scores[departure.playerId] ?? 0,
    };
    delete scores[departure.playerId];
    const throws = { ...state.throws };
    const departedThrow = throws[departure.playerId];
    if (departedThrow) throws[replacementPlayerId] = departedThrow;
    delete throws[departure.playerId];
    return {
      state: {
        ...state,
        players,
        scores,
        throws,
        readyIds: state.readyIds.map((id) =>
          id === departure.playerId ? replacementPlayerId : id,
        ),
      },
      replacementPlayerId,
    };
  },
};
